/**
 * `useAutocompleteSources` — the React adapter over `AutocompleteSourceStore` (editor-chat.md
 * §2.2, §5 step 7).
 *
 * A thin `useSyncExternalStore` bridge: it owns one `AutocompleteSourceStore` for the editor's
 * lifetime, kicks off the initial fetch on mount, tears the store down on unmount, and
 * re-renders the consumer when the cache refreshes. The CodeMirror completion extensions hold
 * the *store* (they read `current()` synchronously at completion time, off the React render
 * path); the React layer holds this hook only so the status affordance can show "tags last
 * refreshed N ago".
 *
 * Keeping the store out of React state is deliberate (editor-chat.md §2.2): completion
 * filtering is local and synchronous — it must not depend on a React render or a round-trip.
 */

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { AutocompleteSourceStore } from './sources';
import type { AutocompleteSources } from '../editorTypes';

/** What the hook returns — the store (for the extensions) + the current snapshot (for the UI). */
export interface UseAutocompleteSourcesResult {
  /** The store — passed into the CM6 completion extensions; they call `current()` synchronously. */
  store: AutocompleteSourceStore;
  /** The current cached sources — re-renders the consumer when the cache refreshes. */
  sources: AutocompleteSources;
}

/**
 * Own an `AutocompleteSourceStore` for the editor's lifetime.
 *
 * The store is created once (a `useRef`) so its identity is stable — the CM6 extensions
 * capture it at editor-construction time and must not see it swap. The initial fetch is
 * kicked off on mount; `dispose()` runs on unmount.
 */
export function useAutocompleteSources(): UseAutocompleteSourcesResult {
  // Create the store exactly once — stable identity for the lifetime of the editor.
  const storeRef = useRef<AutocompleteSourceStore | null>(null);
  if (storeRef.current === null) {
    storeRef.current = new AutocompleteSourceStore();
  }
  const store = storeRef.current;

  useEffect(() => {
    // Kick off the first fetch immediately (non-debounced) so completion has data soon after
    // mount; tear the store down on unmount.
    void store.refreshNow();
    return () => store.dispose();
  }, [store]);

  const sources = useSyncExternalStore(
    (onChange) => store.subscribe(onChange),
    () => store.current(),
    () => store.current(),
  );

  return { store, sources };
}
